import { Input, InputNumber, Typography } from 'antd';
import produce from 'immer';
import { get, merge } from 'lodash';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Checkbox,
  CountryInput,
  DateInput,
  DocumentInput,
  EmailFormItem,
  EmiratesIdInput,
  Form,
  NationalitySelect,
  PhoneNumberFormItem,
  Select,
} from '~/components';
import CustomFields from '~/components/CustomFields';
import { FormItemContextProvider } from '~/components/Form/FormItemContext';
import features from '~/constants/features';
import { useGetDraft } from '~/controllers/draft';
import { useValidator } from '~/controllers/validation';
import { useGetApplication } from '~/controllers/wizard';
import { useOptionalFields } from '~/hooks/useOptionalFields';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { Draft } from '~/types/Draft';
import { NameFn } from '~/utils/buildNamePath';
import createUid from '~/utils/createUid';
import { getStagePayload } from '~/utils/getStagePayload';
import { isReadOnly } from '~/utils/isReadOnly';
import i18n from '~/i18n';

interface Props {
  n: NameFn;
  onFinish: (values: ApplicationPayload) => any;
  initialValues?: any;
}

const getProofOptions: any = () => [
  {
    label: i18n.t('Person name is listed as Authorized Signatory on Company license', { ns: 'fillApplication' }),
    value: 'Person name is listed as manager on Company license',
  },
  {
    label: i18n.t(uploadPowerOfAttorney, { ns: 'fillApplication' }),
    value: uploadPowerOfAttorney,
  },
];

export const uploadPowerOfAttorney = 'Upload a copy of valid notarized Power of Attorney of authorized signatory';
const passport = 'passport';
const emiratesId = 'emiratesId';
const proof = 'proof';
export const authorizedRepresentativeCommonNames = {
  firstName: ['firstName'],
  lastName: ['lastName'],
  passport: [passport],
  emiratesId: [emiratesId],
  designation: [proof, 'designation'],
};

const AuthorizedRepresentativeForm: React.FC<Props> = (props) => {
  const { onFinish, initialValues, n } = props;
  const { t } = useTranslation('fillApplication');
  const [form] = Form.useForm<ApplicationPayload>();
  const validatorController = useValidator(form.getFieldsValue());
  const applicationQuery = useGetApplication();
  const draftQuery = useGetDraft<Draft<any>>();
  const optionalFieldsController = useOptionalFields({ ...initialValues });
  const stagePayload = getStagePayload.wizard(applicationQuery.data);

  return (
    <FormItemContextProvider
      value={{
        defaultValidator: validatorController.validator,
        applicationPayload: applicationQuery.data?.payload,
        draftPayload: draftQuery.data,
        optionalFields: optionalFieldsController.optionalFields ?? {},
        errorFields: stagePayload?.activeStep.errorInformation?.errorFields,
        enabledFields: stagePayload?.activeStep.errorInformation?.enabledFields,
        disabledByDefault: isReadOnly(applicationQuery.data),
        disabled: isReadOnly(applicationQuery.data),
      }}
    >
      <Form
        form={form}
        name={formName}
        onFinish={onFinish}
        onValuesChange={(_, newValues) => {
          optionalFieldsController.debouncedUpdate(newValues);
        }}
        initialValues={{ authorizedRepresentative: initialValues }}
        // initialValues={{
        //   authorizedRepresentative: [
        //     {
        //     },
        //     {
        //       passport: {
        //         files: [
        //           {
        //             id: 'M2Y1YjYyZTMtZWU3Ni00ODU5LWIzNjUtMjFmYjE3YzFmZDBmODY2Yzk3ZGEtYmEzNC00MzI4LWI2ZmUtNGY3ODZlZWJhOTg3P2FjY2Vzc0tleT1iYjU1Yjk1MjEwNGJjYmQxOGViZjE5ZmEyNmQ0ODhmNGM5Njk0ZWRjOWZkMDA2MDUyMjEyOTBlYmIzMTJkYjBk',
        //             mimeType: 'application/pdf',
        //             name: '20250429 copy.pdf',
        //             container: 'sob-ni-1873',
        //           },
        //         ],
        //       },
        //     },
        //   ],
        // }}
      >
        <Form.Item name={n(['uid'])} hidden initialValue={get(initialValues, n(['data', 'uid'])) || createUid()} />
        <Form.Item
          hidden
          label={t('contactId')}
          name={n(['contactId'])}
          initialValue={get(initialValues, n(['contactId'])) || createUid()}
        />
        <Form.Item
          hidden
          label={t('userRoles')}
          name={n(['userRoles'])}
          initialValue={get(initialValues, n(['userRoles'])) || ['authorizedRepresentative']}
        />
        <Form.Item
          hidden
          label={t('matchContactId')}
          name={n(['matchContactId'])}
          initialValue={get(initialValues, n(['matchContactId'])) || null}
        />
        <Form.Item hidden name={n(['salesforceFields'])} initialValue={get(initialValues, n(['salesforceFields']))} />
        <Form.Item hidden name={n(['salesForceUser'])} initialValue={get(initialValues, n(['salesForceUser']))} />
        <Section>
          <Typography.Title level={4}>{t('Personal details').toUpperCase()}</Typography.Title>
          <InputsGroupLayout>
            <Form.Item label={t('First name')} name={n(['firstName'])}>
              <Input />
            </Form.Item>
            <Form.Item label={t('Last name')} name={n(['lastName'])}>
              <Input />
            </Form.Item>
            <Form.Item label={t('Date of birth')} name={n(['dateOfBirth'])}>
              <DateInput disableDateRule="date <= today" />
            </Form.Item>
            <Form.Item label={t('Nationality')} name={n(['nationality'])}>
              <NationalitySelect />
            </Form.Item>
            <Form.Item name={n(['isAuthorizedSignatory'])} valuePropName="checked">
              <Checkbox>{t('Legally Authorized Signatory')}</Checkbox>
            </Form.Item>
            <Form.Item
              name={n(['pep', 'isPep'])}
              valuePropName="checked"
              initialValue={get(initialValues, n(['pep', 'isPep'])) ?? false}
            >
              <Checkbox>{t('Politically Exposed Person (PEP)')}</Checkbox>
            </Form.Item>
          </InputsGroupLayout>
          <Form.Item hidden name={n(['pep', 'files'])} initialValue={get(initialValues, n(['pep', 'files'])) ?? []} />
          <Form.Visible name={n(['pep', 'isPep'])}>
            <Typography.Title level={4}>{t('PEP Document').toUpperCase()}</Typography.Title>
            <Form.Item name={n(['pep', 'files'])} initialValue={get(initialValues, n(['pep', 'files'])) ?? []}>
              <DocumentInput name="pep" variant="default" />
            </Form.Item>
          </Form.Visible>
        </Section>

        <Section>
          <Typography.Title level={4}>{t('Passport').toUpperCase()}</Typography.Title>

          <Typography.Paragraph>
            <Trans
              t={t}
              defaults="Please upload copy of <strong>passport main photo page</strong>  and complete the details in the fields below."
            />
          </Typography.Paragraph>
          <Form.Item name={n([passport, 'files'])}>
            <DocumentInput name="photos" variant="default" />
          </Form.Item>

          <Typography.Title level={5}>{t('Passport details:')}</Typography.Title>
          <InputsGroupLayout>
            <Form.Item validateFirst label={t('Document number')} name={n([passport, 'documentNumber'])}>
              <Input />
            </Form.Item>
            <Form.Item label={t('Passport expiry date')} name={n([passport, 'expiryDate'])}>
              <DateInput disableDateRule="date > today" />
            </Form.Item>
          </InputsGroupLayout>
        </Section>

        <Section formItemLast>
          <Typography.Title level={4}>{t('Emirates ID').toUpperCase()}</Typography.Title>

          <Typography.Paragraph>
            <Trans
              t={t}
              defaults="Please upload copy of both the<strong>  front and back </strong>of the Emirates ID."
            />
          </Typography.Paragraph>
          <Form.Item name={n([emiratesId, 'files'])}>
            <DocumentInput name="photos" variant="default" maxCount={2} />
          </Form.Item>

          <Typography.Title level={5}>{t('Emirates ID details:')}</Typography.Title>
          <InputsGroupLayout>
            <Form.Item label={t('Emirates ID number')} name={n([emiratesId, 'documentNumber'])}>
              <EmiratesIdInput />
            </Form.Item>
            <Form.Item label={t('Emirates ID expiry date')} name={n([emiratesId, 'expiryDate'])}>
              <DateInput disableDateRule="date > today" />
            </Form.Item>
          </InputsGroupLayout>
        </Section>

        <Section formItemLast>
          <Typography.Title level={4}>{t('UAE residency & contact details').toUpperCase()}</Typography.Title>
          <InputsGroupLayout>
            <Form.Item initialValue={features.defaultCountrySalesforce} label={t('Country')} name={n(['country'])}>
              <CountryInput />
            </Form.Item>
            <PhoneNumberFormItem name={n(['mobileNo'])} optional />
            <EmailFormItem name={n(['email'])} />
          </InputsGroupLayout>
        </Section>

        <Form.Visible name={n(['isAuthorizedSignatory'])}>
          <Section formItemLast>
            <Typography.Title level={4}>{t('Authorised Signatory Details').toUpperCase()}</Typography.Title>
            <InputsGroupLayout>
              <Form.Item label={t('Designation')} name={n(authorizedRepresentativeCommonNames.designation)}>
                <Input />
              </Form.Item>
            </InputsGroupLayout>
            <Form.Item label={t('Select the document type')} name={n([proof, 'documentType'])}>
              <Select className="AuthorizedRepresentativeFormItems-DocumentTypeSelect" options={getProofOptions()} />
            </Form.Item>
            <Form.Item hidden initialValue={[]} name={n([proof, 'files'])} />

            <Form.Visible name={n([proof, 'documentType'])} visibleIf={uploadPowerOfAttorney}>
              <Form.Item initialValue={[]} name={n([proof, 'files'])}>
                <DocumentInput name="photos" variant="default" />
              </Form.Item>
            </Form.Visible>
          </Section>
        </Form.Visible>

        <section>
          <CustomFields nFn={(name) => n(['data', ...[name].flat()])} />
        </section>
      </Form>
    </FormItemContextProvider>
  );
};

export const formName = 'addNewAuthorizedRepresentative';

export default AuthorizedRepresentativeForm;
